<template>
    <v-container>
      <v-row align="center" dense>
        <v-col cols="12" sm="3" class="text-center text-sm-left order-2 order-sm-1">
          <v-btn v-if="$route.query.from" @click="goBack" :block="$vuetify.breakpoint.xsOnly">
            <v-icon left>mdi-arrow-left</v-icon>
            Zurück zum Charakter
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" class="order-1 order-sm-2">
          <h1 class="text-center">Schmiede Item Shop</h1>
        </v-col>
        <v-col cols="12" sm="3" class="text-center text-sm-right order-3 order-sm-3">
          <v-btn
            color="amber-darken-2"
            variant="tonal"
            prepend-icon="mdi-help-circle"
            @click="showXpInfo = true"
            :block="$vuetify.breakpoint.xsOnly"
          >
            Wie verdiene ich ThoFu-XP?
          </v-btn>
        </v-col>

        <v-col cols="12" class="text-center order-4 order-sm-4">
          <user-stats-bar
            :username="$store.state.username"
            :xp-name="$store.state.xp_name_global ?? 'XP'"
            :xp-value="$store.state.xp_global ?? 0"
          />
        </v-col>
      </v-row>
      <div>

        <!-- XP Info Dialog -->
        <v-dialog
          v-model="showXpInfo"
          max-width="680"
          scrollable
          :fullscreen="$vuetify.breakpoint.xsOnly"
          transition="dialog-bottom-transition"
        >
          <v-card class="xp-info-dialog">
            <v-card-title class="d-flex align-center pa-4 xp-dialog-header">
              <v-icon color="amber" size="28" class="mr-2">mdi-star-circle</v-icon>
              <span class="text-h5 font-weight-bold">Wie verdiene ich ThoFu-XP?</span>
              <v-spacer />
              <v-btn icon variant="text" @click="showXpInfo = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-4">

              <!-- Tick-System -->
              <v-card class="mb-4 info-block tick-block" variant="outlined">
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-icon color="blue-lighten-2" size="24" class="mr-2">mdi-timer-outline</v-icon>
                    <span class="text-subtitle-1 font-weight-bold">So verdienst du ThoFu-XP</span>
                  </div>
                  <div class="d-flex align-center flex-wrap gap-3 mt-3 tick-stat-row">
                    <div class="tick-stat">
                      <div class="tick-stat__value text-amber">alle 10 Min</div>
                      <div class="tick-stat__label">wird gewertet</div>
                    </div>
                    <v-icon color="grey-lighten-1" size="20" class="tick-arrow">mdi-arrow-right</v-icon>
                    <div class="tick-stat">
                      <div class="tick-stat__value text-green">+100</div>
                      <div class="tick-stat__label">ThoFu-XP</div>
                    </div>
                    <v-icon color="grey-lighten-1" size="20" class="tick-arrow">mdi-arrow-right</v-icon>
                    <div class="tick-stat">
                      <div class="tick-stat__value text-blue-lighten-2">144×</div>
                      <div class="tick-stat__label">pro Tag möglich</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Tageslimit -->
              <v-card class="mb-4 info-block limit-block" variant="outlined">
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-icon color="red-lighten-2" size="24" class="mr-2">mdi-shield-alert-outline</v-icon>
                    <span class="text-subtitle-1 font-weight-bold">Globales Tageslimit</span>
                  </div>
                  <p class="mb-3 text-body-2 text-grey-lighten-1">
                    Pro Tag werden maximal <strong class="text-white">20 Wertungen</strong> (alle 10 Minuten) gezählt.
                    Nach der 20. Wertung werden <strong class="text-red-lighten-2">keine weiteren ThoFu-XP</strong>
                    gutgeschrieben — weder Basis noch Rüstungsbonus.
                  </p>

                  <div class="wertung-bar">
                    <div
                      v-for="n in 20"
                      :key="n"
                      class="wertung-dot wertung-dot--active"
                    />
                  </div>
                  <div class="d-flex justify-space-between flex-wrap gap-1 mt-1">
                    <span class="text-caption text-grey">1. Wertung</span>
                    <span class="text-caption text-red-lighten-2 font-weight-bold">20. Wertung = Tageslimit</span>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Set-Bonus -->
              <v-card class="mb-4 info-block bonus-block" variant="outlined">
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-icon color="amber" size="24" class="mr-2">mdi-crown</v-icon>
                    <span class="text-subtitle-1 font-weight-bold">Set-Bonus — mehr ThoFu-XP pro Wertung</span>
                  </div>
                  <p class="text-body-2 text-grey-lighten-1 mb-2">
                    Vollständige Rüstungssets geben dir bei jeder der 20 täglichen Wertungen
                    <strong class="text-amber">zusätzliche ThoFu-XP</strong> on top.
                    Der Bonus zählt <strong class="text-amber">nicht</strong> zum Wertungslimit —
                    du erhältst immer genau 20 Wertungen pro Tag.
                  </p>
                  <v-alert
                    type="warning"
                    variant="tonal"
                    density="compact"
                    icon="mdi-alert-outline"
                  >
                    Nach der 20. Wertung endet alles der Rüstungsbonus läuft
                    <strong>nicht</strong> als 21. Wertung weiter.
                  </v-alert>
                </v-card-text>
              </v-card>

              <!-- Beispiel -->
              <v-card class="info-block example-block" variant="outlined">
                <v-card-text>
                  <div class="d-flex align-center mb-3">
                    <v-icon color="green-lighten-2" size="24" class="mr-2">mdi-calculator-variant-outline</v-icon>
                    <span class="text-subtitle-1 font-weight-bold">Beispiel: Komplettes Lederrüstungsset</span>
                  </div>

                  <div class="example-table">
                    <div class="example-row">
                      <span class="example-label">Basis ThoFu-XP pro Wertung</span>
                      <span class="example-value text-green">+100 ThoFu-XP</span>
                    </div>
                    <div class="example-row example-row--highlight">
                      <span class="example-label">Lederrüstungs-Bonus pro Wertung</span>
                      <span class="example-value text-amber">+12 ThoFu-XP</span>
                    </div>
                    <div class="example-row">
                      <span class="example-label">Gesamt pro Wertung</span>
                      <span class="example-value text-white font-weight-bold">112 ThoFu-XP</span>
                    </div>
                    <v-divider class="my-2" />
                    <div class="example-row">
                      <span class="example-label">Tagesmaximum ohne Rüstung</span>
                      <span class="example-value text-grey-lighten-1">20 × 100 = <strong>2.000 ThoFu-XP</strong></span>
                    </div>
                    <div class="example-row example-row--total">
                      <span class="example-label">Tagesmaximum mit Lederrüstung</span>
                      <span class="example-value text-green-lighten-1 text-h6 font-weight-bold">20 × 112 = 2.240 ThoFu-XP</span>
                    </div>
                  </div>

                  <v-alert
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mt-3"
                    icon="mdi-lightbulb-outline"
                  >
                    Je besser das Rüstungsset, desto mehr ThoFu-XP holst du aus deinen 20 täglichen Wertungen!
                  </v-alert>
                </v-card-text>
              </v-card>

            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-3">
              <v-spacer />
              <v-btn color="amber-darken-2" variant="flat" @click="showXpInfo = false">Verstanden!</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-app>
          <v-container>
            <div
              v-for="(items, setKey) in equipmentGrouped"
              :key="setKey"
              class="mb-4"
            >
  <v-card @click="toggleSet(setKey)" class="pa-3" outlined>
    <div class="d-flex justify-space-between align-center">
      <h3>{{ getSetName(setKey) }}</h3>
      <v-icon>{{ openedSets.includes(setKey) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </div>
  </v-card>

  <v-expand-transition>
    <div v-show="openedSets.includes(setKey)">
      <v-row class="mt-2" align="stretch">
        <v-col
          v-for="item in items"
          :key="item.id"
          cols="12"
          sm="6"
          md="3"
          class="d-flex"
        >
  <v-card class="pa-3 d-flex flex-column justify-space-between flex-grow-1 equipment-shop-card" elevation="2">
    <div class="reward-header">
    <div class="d-flex justify-space-between align-start mb-2">
      <div class="flex-grow-1 pr-2" style="min-width: 0">
        <v-card-title class="text-h6 py-1 mb-1 text-truncate">{{ item.name }}</v-card-title>
        <v-card-subtitle class="py-1 my-0 font-weight-medium">Item Kosten: {{ item.price.toLocaleString('de-DE') }}</v-card-subtitle>
        <v-card-subtitle class="py-1 my-0">Item Set-Bonus: +{{ item.bonus }} {{ $store.state.xp_name_global ?? 'XP' }}
          <span class="text-yellow">(nur bei 4/4 Teilen des selben Sets)</span>
        </v-card-subtitle>
        <v-card-text>
          <div class="pa-2" style="border: 1px solid #ccc; border-radius: 6px; background: grey;">
            <span>Item Slots:</span>
            <div v-for="(line, index) in formattedSlots(item.slots)" :key="index">
              {{ line }}
            </div>
          </div>
        </v-card-text>
      </div>
      <v-img
        :src="getItemImage(item.shop_img)"
        class="ml-2 equipment-shop-image"
        contain
      />
    </div>
  </div>

    <div class="reward-content">

    <v-card-text>{{ item.prompt }}</v-card-text>
    </div>

    <v-card-actions class="mt-3 reward-actions">
      <v-btn
    color="primary"
    @click="buyEquipment(item)"
    :disabled="isItemDisabled(item)"
    :block="$vuetify.breakpoint.xsOnly"
  >
  {{ isItemDisabled(item) ? 'Bereits gekauft ✅' : 'Kaufen' }}
  </v-btn>
    </v-card-actions>
  </v-card>

              </v-col>
            </v-row>
          </div>
  </v-expand-transition>
</div>

          </v-container>
        </v-app>
      </div>
      <v-snackbar v-model="errorReedemReward" timeout="5000" color="red">
        {{ dynamicErrorMsg }}

        <template v-slot:actions>
          <v-btn color="pink" variant="text" @click="errorReedemReward = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
      <v-snackbar v-model="ReedemReward" timeout="3000" color="green">
        {{ dynamicReedemMsg }}

        <template v-slot:actions>
          <v-btn color="pink" variant="text" @click="ReedemReward = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>

<script>

import BackendService from '@/services/BackendService';
import UserStatsBar from '@/components/UserStatsBar.vue';

export default {
  name: 'LoayltiyRewardsView',
  components: {
    UserStatsBar,
  },

  data: () => ({
    sendData: {},
    settings: [],
    openedSets: [],
    showXpInfo: false,
    equipment_raw: [],
    currentIndex: null,
    selectReward: null,
    errorReedemReward: false,
    dynamicErrorMsg: '',
    ReedemReward: false,
    dynamicReedemMsg: '',
    currentReedemRewards: [],
    redeemCounts: {},
    waiting: 0,
    socket: null,
    sessionId: {
      type: String,
      required: true,
    },
  }),
  mounted() {
    console.log(this.$route.path);
    BackendService.loadEquipmentListGlobal().then((data) => {
      console.log(data);
      this.equipment_raw = Array.isArray(data.data) ? data.data : [];
    }).catch(() => {});
  },
  computed: {
    equipmentGrouped() {
      const positionOrder = ['head', 'torso', 'legs', 'feets'];

      // 1. Items filtern
      const items = this.equipment_raw
        .filter((item) => item.price !== 0);

      // 2. Nach Set gruppieren
      const sets = {};

      items.forEach((item) => {
        if (!sets[item.set]) {
          sets[item.set] = [];
        }
        sets[item.set].push(item);
      });

      // 3. Innerhalb jedes Sets nach Position sortieren
      Object.keys(sets).forEach((setKey) => {
        sets[setKey] = sets[setKey].sort(
          (a, b) => positionOrder.indexOf(a.position) - positionOrder.indexOf(b.position),
        );
      });

      return sets;
    },
  },
  methods: {
    goBack() {
      this.$router.push({
        name: 'equipment_selection',
        params: { id: this.$route.query.from },
      });
    },
    formattedSlots(slots) {
      if (!slots || !slots.length) return ['Keine Slots vorhanden'];

      // Prüfen ob nur ein leerer Slot existiert
      if (slots.length === 1 && !slots[0].type) {
        return ['Keine Slots vorhanden'];
      }

      return slots.map((s, i) => `Slot ${i + 1} - Type: ${s.type} – Max Rarity: ${s.max_rarity}`);
    },
    toggleSet(key) {
      if (this.openedSets.includes(key)) {
        this.openedSets = this.openedSets.filter((k) => k !== key);
      } else {
        this.openedSets.push(key);
      }
    },
    getSetName(key) {
      const names = {
        1: 'Gewebtes Tuch Set',
        2: 'Stoffrüstungsset',
        3: 'Lederrüstungsset',
      };
      return names[key] || `Set ${key}`;
    },
    isItemDisabled(item) {
      return (
        item.purchase === 1
      );
    },
    getItemImage(path) {
      if (path === null || path === '') {
        console.log('Bild Null');
        return '/favicon-32x32.png';
      }
      return `/img/forge_shop/${path}.png`;
    },
    buyEquipment(item) {
      BackendService.buyEquipment(item).then((data) => {
        console.log(data);
        this.equipment_raw = data.data.items;
        this.dynamicReedemMsg = 'Item erfolgreich gekauft!';
        this.ReedemReward = true;
        this.$store.commit('set_xp_global', data.data.xp);
      }).catch((error) => {
        // console.log(error.response.status);
        // console.log(error.response.data);
        if (error.response.status === 409 && error.response.data === 'user_has_not_enough_xp') {
          this.dynamicErrorMsg = 'Nicht genügend XP für diese Transaktion!';
          this.errorReedemReward = true;
        } else if (error.response.status === 409 && error.response.data === 'user_has_item') {
          this.dynamicErrorMsg = 'Du hast diese Item bereits gekauft!';
          this.errorReedemReward = true;
        }
      });
    },
  },
};
</script>
<style lang="scss" scoped>
  .giveaway {
    &_header {
      background-color: darkblue;
      color: white;
      text-align: center;
      width: 50vw;
      margin: 10px auto 0 auto;
    }

    &_body {
      text-align: center;
      width: 50vw;
      margin: 10px auto 0 auto;
    }
  }

  .card {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .card-actions {
    margin-top: auto;
    /* Setzt die Margin für den oberen Bereich auf "auto" */
  }

  .v-card:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.3s ease;
  }

  .reward-card {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* variabler Bereich */
  .reward-content {
    flex-grow: 1;
  }

  /* Button immer unten */
  .reward-actions {
    margin-top: auto;
  }

  .text-yellow {
    color: rgb(255, 123, 0);
  }

  .xp-info-dialog {
    background: #1e1e2e;
  }

  .xp-dialog-header {
    background: linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%);
  }

  .info-block {
    border-radius: 10px !important;
  }

  .tick-block {
    border-color: rgba(100, 160, 255, 0.3) !important;
    background: rgba(100, 160, 255, 0.05);
  }

  .limit-block {
    border-color: rgba(255, 100, 100, 0.3) !important;
    background: rgba(255, 100, 100, 0.05);
  }

  .bonus-block {
    border-color: rgba(255, 193, 7, 0.3) !important;
    background: rgba(255, 193, 7, 0.05);
  }

  .example-block {
    border-color: rgba(100, 220, 130, 0.3) !important;
    background: rgba(100, 220, 130, 0.05);
  }

  .tick-stat {
    text-align: center;
    min-width: 70px;

    &__value {
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1.2;
    }

    &__label {
      font-size: 0.72rem;
      color: rgba(255, 255, 255, 0.55);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  .example-table {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .example-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.04);

    &--total {
      background: rgba(100, 220, 130, 0.08);
      padding: 8px 10px;
    }

    &--highlight {
      background: rgba(255, 193, 7, 0.08);
      border-left: 3px solid rgba(255, 193, 7, 0.5);
    }
  }

  .wertung-bar {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .wertung-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &--active {
      background: rgba(239, 83, 80, 0.45);
      border-color: rgba(239, 83, 80, 0.7);
    }

    &:last-child {
      background: #ef5350;
      border-color: #ef5350;
      box-shadow: 0 0 6px rgba(239, 83, 80, 0.7);
    }
  }

  .example-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
  }

  .example-value {
    font-weight: 600;
    font-size: 0.9rem;
  }

  @media (max-width: 599px) {
    .tick-stat-row {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }

    .tick-stat {
      min-width: 0;
    }

    .tick-arrow {
      align-self: center;
      transform: rotate(90deg);
    }

    .example-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }

    .xp-dialog-header {
      flex-wrap: wrap;
    }
  }

  .equipment-shop-card {
    width: 100%;
  }

  .equipment-shop-image {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
  }

  @media (max-width: 599px) {
    .equipment-shop-image {
      width: 64px;
      height: 64px;
    }
  }
  </style>
